"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DefaultEscrowAPI = void 0;
const bn_js_1 = __importDefault(require("bn.js"));
const big_js_1 = __importDefault(require("big.js"));
const utils_1 = require("../utils");
const types_1 = require("../types");
class DefaultEscrowAPI {
    constructor(api, governanceCurrency, systemAPI, transactionAPI) {
        this.api = api;
        this.governanceCurrency = governanceCurrency;
        this.systemAPI = systemAPI;
        this.transactionAPI = transactionAPI;
    }
    createLock(amount, unlockHeight) {
        return __awaiter(this, void 0, void 0, function* () {
            const tx = this.api.tx.escrow.createLock(amount.toString(amount.currency.rawBase), unlockHeight);
            yield this.transactionAPI.sendLogged(tx, this.api.events.escrow.Deposit, true);
        });
    }
    withdraw() {
        return __awaiter(this, void 0, void 0, function* () {
            const tx = this.api.tx.escrow.withdraw();
            yield this.transactionAPI.sendLogged(tx, this.api.events.escrow.Withdraw, true);
        });
    }
    withdrawRewards() {
        return __awaiter(this, void 0, void 0, function* () {
            const tx = this.api.tx.escrowAnnuity.withdrawRewards();
            yield this.transactionAPI.sendLogged(tx, this.api.events.escrowRewards.WithdrawReward, true);
        });
    }
    increaseAmount(amount) {
        return __awaiter(this, void 0, void 0, function* () {
            const tx = this.api.tx.escrow.increaseAmount(amount.toString(amount.currency.rawBase));
            yield this.transactionAPI.sendLogged(tx, this.api.events.escrow.Deposit, true);
        });
    }
    increaseUnlockHeight(unlockHeight) {
        return __awaiter(this, void 0, void 0, function* () {
            const tx = this.api.tx.escrow.increaseUnlockHeight(unlockHeight);
            yield this.transactionAPI.sendLogged(tx, this.api.events.escrow.Deposit, true);
        });
    }
    getRewards(accountId) {
        return __awaiter(this, void 0, void 0, function* () {
            // Step 1. Get amount in reward pool for the account ID
            const [rewardStake, rewardPerToken, rewardTally] = yield Promise.all([
                this.getEscrowStake(accountId),
                this.getRewardPerToken(),
                this.getRewardTally(accountId)
            ]);
            // Step 2. Calculate the rewards that can be withdrawn at the moment
            // Stake[currencyId, accountId] * RewardPerToken[currencyId] - RewardTally[currencyId, accountId]
            const rewards = rewardStake.mul(rewardPerToken).sub(rewardTally);
            return (0, utils_1.newMonetaryAmount)(rewards, this.governanceCurrency);
        });
    }
    getRewardEstimate(accountId, amountToLock, blockLockTimeExtension) {
        return __awaiter(this, void 0, void 0, function* () {
            const [userStake, totalStake, blockReward, stakedBalance, currentBlockNumber, minimumBlockPeriod, maxPeriod] = yield Promise.all([
                this.getEscrowStake(accountId),
                this.getEscrowTotalStake(),
                this.getRewardPerBlock(),
                this.getStakedBalance(accountId),
                this.systemAPI.getCurrentBlockNumber(),
                this.api.consts.timestamp.minimumPeriod,
                this.getMaxPeriod()
            ]);
            return this._computeRewardEstimate(userStake, totalStake, blockReward, stakedBalance, currentBlockNumber, minimumBlockPeriod.toNumber(), maxPeriod.toNumber(), amountToLock, blockLockTimeExtension);
        });
    }
    _computeRewardEstimate(userStake, totalStake, blockReward, stakedBalance, currentBlockNumber, minimumBlockPeriod, maxPeriod, amountToLock = (0, utils_1.newMonetaryAmount)(0, this.governanceCurrency), blockLockTimeExtension = 0) {
        // Note: the parachain uses the balance_at which combines the staked amount
        // and staked time divided by the maximum period to calculate the share in the reward pool
        // like `amountLocked * unlockHeight / maxPeriod`
        // https://github.com/interlay/interbtc/blob/1.7.3/crates/escrow/src/lib.rs#L525
        // We need to differentiate four cases:
        // 1. The user does not add any new stake/extend their lock time
        // 2. The user extends the locktime and increases the stake
        // 3. The user increases only their stake
        // 4. The user extends only the locktime
        // Note: on first time staking the user has to provide both a increased stake and a locktime
        // otherwise, the rewards will be 0.
        const newStakedBalance = {
            amount: stakedBalance.amount,
            endBlock: stakedBalance.endBlock
        };
        const monetaryAddedStake = (0, utils_1.newMonetaryAmount)(amountToLock.toBig(), this.governanceCurrency);
        // User staking for the first time; only case 2 relevant otherwise rewards should be 0
        if (stakedBalance.amount.isZero()) {
            newStakedBalance.amount = monetaryAddedStake;
            newStakedBalance.endBlock = currentBlockNumber + blockLockTimeExtension;
            // User increasing stake or locking amount or none (cases 1 - 4)
        }
        else {
            // might add 0 to either amount or endBlock
            newStakedBalance.amount = stakedBalance.amount.add(monetaryAddedStake);
            newStakedBalance.endBlock = stakedBalance.endBlock + blockLockTimeExtension;
        }
        let newLockDuration = 0;
        if (newStakedBalance.endBlock - currentBlockNumber > 0) {
            newLockDuration = newStakedBalance.endBlock - currentBlockNumber;
        }
        const newUserStake = newStakedBalance.amount.toBig()
            .mul(newLockDuration)
            .div(maxPeriod);
        const userStakeDifference = newUserStake.sub(userStake);
        const newTotalStake = totalStake.add(userStakeDifference);
        // Catch 0 values
        if (newLockDuration == 0 ||
            newTotalStake.eq(0) ||
            newStakedBalance.amount.isZero()) {
            return {
                amount: (0, utils_1.newMonetaryAmount)(0, this.governanceCurrency),
                apy: new big_js_1.default(0)
            };
        }
        // Reward amount for the entire time is the newUserStake / netTotalStake * blockReward * lock duration
        const rewardAmount = newUserStake
            .div(newTotalStake)
            .mul(blockReward.toBig())
            .mul(newLockDuration);
        const monetaryRewardAmount = (0, utils_1.newMonetaryAmount)(rewardAmount, this.governanceCurrency);
        // TODO: move this to a util function so we can use it across the codebase
        // normalize APY to 1 year
        const blockTime = minimumBlockPeriod * 2; // ms
        const blocksPerYear = (86400 * 365 * 1000) / blockTime;
        const annualisedReward = rewardAmount
            .div(newLockDuration)
            .mul(blocksPerYear);
        const apy = annualisedReward
            .div(newStakedBalance.amount.toBig())
            .mul(100);
        return {
            amount: monetaryRewardAmount,
            apy: apy
        };
    }
    getEscrowStake(accountId) {
        return __awaiter(this, void 0, void 0, function* () {
            const rawStake = yield this.api.query.escrowRewards.stake(accountId);
            return (0, utils_1.decodeFixedPointType)(rawStake);
        });
    }
    getEscrowTotalStake() {
        return __awaiter(this, void 0, void 0, function* () {
            const rawTotalStake = yield this.api.query.escrowRewards.totalStake();
            return (0, utils_1.decodeFixedPointType)(rawTotalStake);
        });
    }
    getRewardTally(accountId) {
        return __awaiter(this, void 0, void 0, function* () {
            const governanceCurrencyId = (0, utils_1.newCurrencyId)(this.api, (0, types_1.tickerToCurrencyIdLiteral)(this.governanceCurrency.ticker));
            const rawRewardTally = yield this.api.query.escrowRewards.rewardTally(governanceCurrencyId, accountId);
            return (0, utils_1.decodeFixedPointType)(rawRewardTally);
        });
    }
    getRewardPerToken() {
        return __awaiter(this, void 0, void 0, function* () {
            const governanceCurrencyId = (0, utils_1.newCurrencyId)(this.api, (0, types_1.tickerToCurrencyIdLiteral)(this.governanceCurrency.ticker));
            const rawRewardPerToken = yield this.api.query.escrowRewards.rewardPerToken(governanceCurrencyId);
            return (0, utils_1.decodeFixedPointType)(rawRewardPerToken);
        });
    }
    getRewardPerBlock() {
        return __awaiter(this, void 0, void 0, function* () {
            const rawRewardPerBlock = yield this.api.query.escrowAnnuity.rewardPerBlock();
            return (0, utils_1.newMonetaryAmount)(rawRewardPerBlock.toString(), this.governanceCurrency);
        });
    }
    getStakedBalance(accountId) {
        return __awaiter(this, void 0, void 0, function* () {
            const rawStakedBalance = yield this.api.query.escrow.locked(accountId);
            return (0, types_1.parseEscrowLockedBalance)(this.governanceCurrency, rawStakedBalance);
        });
    }
    votingBalance(accountId, blockNumber) {
        return __awaiter(this, void 0, void 0, function* () {
            const height = blockNumber || (yield this.systemAPI.getCurrentBlockNumber());
            const userPointEpoch = yield this.api.query.escrow.userPointEpoch(accountId);
            const lastPoint = yield this.api.query.escrow.userPointHistory(accountId, userPointEpoch);
            const rawBalance = this.rawBalanceAt((0, types_1.parseEscrowPoint)(lastPoint), height);
            // `rawBalance.toString()` is used to convert the BN to a BigSource type
            return (0, utils_1.newMonetaryAmount)(rawBalance.toString(), (0, utils_1.toVoting)(this.governanceCurrency));
        });
    }
    rawBalanceAt(escrowPoint, height) {
        /*
            Rust reference implementation:
            https://github.com/interlay/interbtc/blob/0302612ae5f8ddf1f556042ca347c6104704ad83/crates/escrow/src/lib.rs#L524
        */
        const heightDiff = this.saturatingSub(new bn_js_1.default(height), escrowPoint.ts);
        return this.saturatingSub(escrowPoint.bias, escrowPoint.slope.mul(heightDiff));
    }
    totalVotingSupply(blockNumber) {
        return __awaiter(this, void 0, void 0, function* () {
            const [currentBlockNumber, epoch, span, rawSlopeChanges] = yield Promise.all([
                this.systemAPI.getCurrentBlockNumber(),
                this.api.query.escrow.epoch(),
                this.getSpan(),
                this.api.query.escrow.slopeChanges.entries()
            ]);
            const height = blockNumber || currentBlockNumber;
            const slopeChanges = new Map();
            rawSlopeChanges.forEach(([id, value]) => slopeChanges.set((0, utils_1.storageKeyToNthInner)(id).toBn(), value.toBn()));
            const lastPoint = yield this.api.query.escrow.pointHistory(epoch);
            const rawSupply = this.rawSupplyAt((0, types_1.parseEscrowPoint)(lastPoint), new bn_js_1.default(height), span, slopeChanges);
            // `rawSupply.toString()` is used to convert the BN to a BigSource type
            return (0, utils_1.newMonetaryAmount)(rawSupply.toString(), (0, utils_1.toVoting)(this.governanceCurrency));
        });
    }
    rawSupplyAt(escrowPoint, height, escrowSpan, slopeChanges) {
        /*
            Rust reference implementation:
            https://github.com/interlay/interbtc/blob/0302612ae5f8ddf1f556042ca347c6104704ad83/crates/escrow/src/lib.rs#L530
        */
        const lastPoint = escrowPoint;
        let t_i = this.roundHeight(lastPoint.ts, escrowSpan);
        while (t_i.lt(height)) {
            t_i = t_i.add(escrowSpan);
            // The BN type is handled by polkadot-js in the api call
            let d_slope;
            if (t_i.gt(height)) {
                t_i = height;
                d_slope = new bn_js_1.default(0);
            }
            else {
                d_slope = this.getSlopeChange(slopeChanges, t_i);
            }
            const heightDiff = this.saturatingSub(t_i, lastPoint.ts);
            lastPoint.bias = this.saturatingSub(lastPoint.bias, lastPoint.slope.mul(heightDiff));
            if (t_i.eq(height)) {
                break;
            }
            lastPoint.slope = lastPoint.slope.add(d_slope);
            lastPoint.ts = t_i;
        }
        return lastPoint.bias;
    }
    getSpan() {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield this.api.consts.escrow.span).toBn();
        });
    }
    getMaxPeriod() {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield this.api.consts.escrow.maxPeriod).toBn();
        });
    }
    roundHeight(height, span) {
        return height.div(span).mul(span);
    }
    getSlopeChange(slopeChanges, key) {
        let d_slope = slopeChanges.get(key);
        if (d_slope === undefined) {
            d_slope = new bn_js_1.default(0);
        }
        return d_slope;
    }
    saturatingSub(x, y) {
        return bn_js_1.default.max(x.sub(y), new bn_js_1.default(0));
    }
}
exports.DefaultEscrowAPI = DefaultEscrowAPI;
//# sourceMappingURL=escrow.js.map